// Initial state for the /demo experience. Builds a curated set of campaigns,
// leads, and sites so the dashboard, Leads tab, and Sites tab are populated
// the moment a visitor lands on /demo — no scrape required.
//
// Pure data builder: no React, no side effects. Called once from
// DemoStateProvider's useState lazy initializer.

import {
  generateMockLeads,
  generateReviews,
  getPhotosForNiche,
  NICHE_SERVICES,
  type DemoCampaign,
  type DemoLead,
} from './mockLeadsData';
import { generateMockSiteHtml } from './generateMockSite';
import { renderMonoTypeSite, type TemplateData } from './realTemplates';
import type { DemoSite } from './DemoStateProvider';

// Stable token for the buyer-facing preview demoed in the launch video.
// `/demo/p/strong-fitness` always resolves to this site.
export const STRONG_FITNESS_TOKEN = 'strong-fitness';

interface CampaignBlueprint {
  niche: string;
  nicheLabel: string;
  location: string;
  daysAgo: number;
}

const CAMPAIGN_BLUEPRINTS: CampaignBlueprint[] = [
  { niche: 'plumber',    nicheLabel: 'Plumbing',     location: 'Austin, TX',    daysAgo: 32 },
  { niche: 'salon',      nicheLabel: 'Hair Salons',  location: 'Denver, CO',    daysAgo: 24 },
  { niche: 'restaurant', nicheLabel: 'Restaurants',  location: 'Portland, OR',  daysAgo: 17 },
  { niche: 'dentist',    nicheLabel: 'Dentists',     location: 'Nashville, TN', daysAgo: 11 },
  { niche: 'gym',        nicheLabel: 'Gyms',         location: 'San Diego, CA', daysAgo: 6 },
  { niche: 'photography',nicheLabel: 'Photographers',location: 'Brooklyn, NY',  daysAgo: 2 },
];

// Templates spanning the available palette set so seeded sites look distinct.
const TEMPLATE_CYCLE = [
  'modern-minimal',
  'bold-corporate',
  'warm-friendly',
  'elegant-luxury',
  'tech-startup',
  'playful-creative',
];

// Each seed campaign yields N sites, cycling through this status pattern so
// the Sites tab opens with a believable mix of draft / sent / sold cards.
const SITE_STATUS_CYCLE: Array<'draft' | 'sent' | 'sold'> = ['sold', 'sent', 'draft'];

const DEFAULT_FEATURES = ['contact_form', 'gallery', 'reviews', 'booking'];
const DEFAULT_LAYOUTS = ['hero', 'services', 'gallery', 'reviews', 'contact'];

export interface DemoSeed {
  campaigns: DemoCampaign[];
  leads: DemoLead[];
  sites: DemoSite[];
}

export function buildDemoSeed(): DemoSeed {
  const now = Date.now();
  const DAY = 86_400_000;

  const campaigns: DemoCampaign[] = CAMPAIGN_BLUEPRINTS.map((bp, i) => ({
    id: `demo-camp-seed-${i}`,
    userId: 'demo-user',
    name: `${bp.nicheLabel} in ${bp.location}`,
    niche: bp.niche,
    nicheLabel: bp.nicheLabel,
    location: bp.location,
    status: 'active',
    isLocalCampaign: false,
    maxLeads: 100,
    createdAt: new Date(now - bp.daysAgo * DAY),
    lastScrapedAt: new Date(now - Math.max(bp.daysAgo - 1, 1) * DAY),
  }));

  // Generate 12 leads per campaign for variety on the Leads tab.
  const leads: DemoLead[] = campaigns.flatMap((c) => generateMockLeads(c).slice(0, 12));

  // Build seed sites: 2 per campaign × 6 campaigns = 12 sites.
  // Pick non-adjacent lead indices so the deterministic name generator is
  // very unlikely to produce duplicate business names within the same campaign.
  const LEAD_PICKS_PER_CAMPAIGN = [3, 8];
  const sites: DemoSite[] = [];
  campaigns.forEach((campaign, ci) => {
    const campLeads = leads.filter((l) => l.campaignId === campaign.id);
    for (let si = 0; si < LEAD_PICKS_PER_CAMPAIGN.length; si++) {
      const lead = campLeads[LEAD_PICKS_PER_CAMPAIGN[si]];
      if (!lead) continue;

      const status = SITE_STATUS_CYCLE[(ci + si) % SITE_STATUS_CYCLE.length];
      const template = TEMPLATE_CYCLE[(ci * 2 + si) % TEMPLATE_CYCLE.length];
      const serviceNames = NICHE_SERVICES[lead.niche] || [
        'Quality service', 'Professional team', 'Local expertise', 'Fair pricing',
      ];
      const niceNiche = lead.niche.replace(/_/g, ' ');
      const html = generateMockSiteHtml({
        lead,
        templateType: template,
        selectedFeatures: DEFAULT_FEATURES,
        services: serviceNames,
      });

      // Time-shift each site backwards so the dashboard timeline isn't flat.
      const ageDays = Math.max(1, Math.floor((ci * 4 + si * 2 + 2)));
      const createdAt = new Date(now - ageDays * DAY);
      const updatedAt = new Date(now - Math.max(ageDays - 1, 0) * DAY);
      const soldAt = status === 'sold' ? new Date(now - Math.max(ageDays - 2, 0) * DAY) : undefined;

      sites.push({
        id: `demo-site-seed-${ci}-${si}`,
        userId: 'demo-user',
        leadId: lead.id,
        name: lead.businessName,
        templateType: template as DemoSite['templateType'],
        status,
        views: status === 'sold' ? 38 + ci * 17 + si * 6 : status === 'sent' ? 9 + ci * 4 + si * 3 : 0,
        generatedHtml: html,
        content: {
          headline: `${lead.businessName} — trusted ${niceNiche} in ${lead.location}`,
          aboutText: `${lead.businessName} has been serving ${lead.location} and surrounding areas with reliable ${niceNiche} service. Honest pricing, real expertise, and work that lasts.`,
          services: serviceNames.map((name) => ({
            name,
            description: `Professional ${name.toLowerCase()} done right the first time.`,
          })),
          testimonials: lead.googleReviews.slice(0, 3).map((r) => ({
            name: r.authorName,
            content: r.text,
            rating: r.rating,
          })),
          seo: {
            title: `${lead.businessName} | ${niceNiche} in ${lead.location}`,
            description: `Professional ${niceNiche} services in ${lead.location}.`,
          },
          faq: [],
          videoUrl: '',
        },
        siteMeta: {
          templateId: template,
          sitePersonality: 'professional',
          primaryAngle: `Trusted local ${niceNiche} in ${lead.location}`,
          sectionsUsed: DEFAULT_LAYOUTS,
        },
        businessData: {
          phone: lead.phone,
          rating: lead.rating,
          reviewCount: lead.reviewCount ?? 0,
          businessHours: lead.businessHours,
          photos: lead.businessImages,
          googleMapsUrl: lead.googleMapsUrl,
          googleReviews: lead.googleReviews,
        },
        selectedFeatures: DEFAULT_FEATURES,
        selectedLayouts: DEFAULT_LAYOUTS,
        previewToken: `demo-token-seed-${ci}-${si}`,
        price: status === 'sold' ? 49900 : status === 'sent' ? 39900 : 29900,
        createdAt,
        updatedAt,
        ...(soldAt ? { soldAt } : {}),
      });

      // Mirror the lead-status side-effects that generateSite/markSiteSold do.
      lead.siteGenerated = true;
      lead.status = status === 'sold' ? 'converted' : 'contacted';
    }
  });

  // Hand-built "Strong Fitness" lead + site for the buyer-preview demo.
  // Guaranteed name, fixed $849 price, and a stable preview token so the
  // launch video can always open /demo/p/strong-fitness.
  const gymCampaign = campaigns.find((c) => c.niche === 'gym') ?? campaigns[0];
  const gymServices = NICHE_SERVICES.gym;
  const strongFitnessLead: DemoLead = {
    id: 'demo-lead-strong-fitness',
    userId: 'demo-user',
    campaignId: gymCampaign.id,
    businessName: 'Strong Fitness',
    niche: 'gym',
    location: gymCampaign.location,
    contactMethod: 'email',
    email: 'info@strongfitness.com',
    emailConfidence: 0.92,
    emailSource: 'pattern',
    phone: '(619) 555-0142',
    phoneNormalized: '6195550142',
    smsCapable: true,
    rating: 4.9,
    reviewCount: 187,
    hasWebsite: false,
    siteGenerated: true,
    googlePlaceId: 'demo-place-strong-fitness',
    businessImages: getPhotosForNiche('gym', 1),
    googleReviews: generateReviews('Strong Fitness', 'gym', 4),
    businessHours: ['Mon-Fri 5am-10pm', 'Sat 7am-8pm', 'Sun 8am-6pm'],
    googleMapsUrl: 'https://maps.google.com/?q=Strong+Fitness+San+Diego',
    status: 'contacted',
    createdAt: new Date(now - 5 * DAY),
  };
  leads.push(strongFitnessLead);

  // The buyer preview must render the EXACT production "Mono Type" template,
  // so build proper TemplateData and render it with the vendored real template
  // instead of the simplified demo generator.
  const sfTemplate = 'mono-type';
  const sfHeadline = "San Diego's home for serious strength training";
  const sfAbout = `Strong Fitness has helped San Diego athletes get stronger for over a decade. Coaches who care, equipment that delivers, and a community that pushes you every session.`;
  const sfServices = gymServices.map((name) => ({
    name,
    description: `Professional ${name.toLowerCase()} done right the first time.`,
  }));
  const sfTestimonials = strongFitnessLead.googleReviews.slice(0, 3).map((r) => ({
    name: r.authorName,
    content: r.text,
    rating: r.rating,
  }));
  const sfSeo = {
    title: 'Strong Fitness | Gym in San Diego, CA',
    description: 'Professional gym and personal training in San Diego.',
  };
  const sfTemplateData: TemplateData = {
    businessName: strongFitnessLead.businessName,
    headline: sfHeadline,
    aboutText: sfAbout,
    services: sfServices,
    testimonials: sfTestimonials,
    seo: sfSeo,
    businessData: {
      phone: strongFitnessLead.phone,
      rating: strongFitnessLead.rating,
      reviewCount: strongFitnessLead.reviewCount ?? 0,
      businessHours: strongFitnessLead.businessHours,
      photos: strongFitnessLead.businessImages,
      googleMapsUrl: strongFitnessLead.googleMapsUrl,
      address: strongFitnessLead.location,
      contactEmail: strongFitnessLead.email,
    },
    faq: [],
  };

  const strongFitnessSite: DemoSite = {
    id: 'demo-site-strong-fitness',
    userId: 'demo-user',
    leadId: strongFitnessLead.id,
    name: strongFitnessLead.businessName,
    templateType: sfTemplate as DemoSite['templateType'],
    status: 'sent',
    views: 23,
    generatedHtml: renderMonoTypeSite(sfTemplateData),
    content: {
      headline: sfHeadline,
      aboutText: sfAbout,
      services: sfServices,
      testimonials: sfTestimonials,
      seo: sfSeo,
      faq: [],
      videoUrl: '',
    },
    siteMeta: {
      templateId: sfTemplate,
      sitePersonality: 'professional',
      primaryAngle: 'Serious strength training in San Diego',
      sectionsUsed: DEFAULT_LAYOUTS,
    },
    businessData: {
      phone: strongFitnessLead.phone,
      rating: strongFitnessLead.rating,
      reviewCount: strongFitnessLead.reviewCount ?? 0,
      businessHours: strongFitnessLead.businessHours,
      photos: strongFitnessLead.businessImages,
      googleMapsUrl: strongFitnessLead.googleMapsUrl,
      googleReviews: strongFitnessLead.googleReviews,
    },
    selectedFeatures: DEFAULT_FEATURES,
    selectedLayouts: DEFAULT_LAYOUTS,
    previewToken: STRONG_FITNESS_TOKEN,
    price: 84900,
    createdAt: new Date(now - 4 * DAY),
    updatedAt: new Date(now - 1 * DAY),
  };
  // Surface it first on the Sites tab so it's easy to find while filming.
  sites.unshift(strongFitnessSite);

  return { campaigns, leads, sites };
}
